import { createBlocksClient } from '@seliseblocks/client';
import { useAuthStore } from '@/state/store/auth';
import { isInvalidRefreshTokenError, refreshAndStoreTokens } from '@/lib/https';

/**
 * Single `@seliseblocks/client` instance for this app.
 *
 * This app predates the `blocks new web` scaffold that normally wires this
 * file up automatically -- it talks to Blocks Cloud through a hand-rolled
 * `clients` HTTP wrapper (`src/lib/https.ts`) plus a Zustand auth store
 * instead. The SDK's `BlocksClientConfig` is built specifically to slot into
 * an existing auth flow like that one: `accessToken` and `onUnauthorized`
 * are caller-owned resolvers, and the SDK "never stores, refreshes, or
 * clears" the token itself. So rather than let the SDK manage its own
 * session (which would race the existing store), this client reads the same
 * Zustand-held access token `https.ts` uses, and recovers a 401 with the
 * same in-flight refresh `https.ts` runs -- one session, one source of
 * truth, two HTTP layers.
 */

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');
/** Where IAM returns the hosted (OIDC) login; registered on the Blocks OIDC client. */
const OIDC_CALLBACK_PATH = '/login/oidc';

export const blocksClient = createBlocksClient({
  apiUrl: API_BASE_URL,
  xBlocksKey: import.meta.env.VITE_X_BLOCKS_KEY ?? '',
  // Sign-in is the hosted (OIDC) login (`auth.idp.*`) of the Blocks OIDC client.
  oidc: {
    url: API_BASE_URL,
    clientId: import.meta.env.VITE_BLOCKS_OIDC_CLIENT_ID ?? '',
    redirectUri: `${window.location.origin}${OIDC_CALLBACK_PATH}`,
    scope: 'openid offline_access',
  },
  accessToken: () => useAuthStore.getState().accessToken ?? undefined,
  onUnauthorized: async () => {
    try {
      // Same in-flight refresh as `https.ts`, so a 401 here and one there
      // during app load share one refresh call.
      return await refreshAndStoreTokens();
    } catch (error) {
      // Only a rejected refresh token should force a logout -- a transient
      // network/5xx failure should leave the session alone so the next call
      // can retry, matching `https.ts`'s `handleAuthError`.
      if (isInvalidRefreshTokenError(error)) {
        useAuthStore.getState().logout();
      }
      return undefined;
    }
  },
});
